/**
 * 复制指令
 * 用于复制文本到剪贴板
 * @author 系统
 * @date 2024-01-01
 */

import type { Directive, DirectiveBinding } from 'vue'
import { ElMessage } from 'element-plus'

/**
 * 复制文本到剪贴板
 * @param text 要复制的文本
 * @returns 是否复制成功
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // 使用现代 Clipboard API
      await navigator.clipboard.writeText(text)
      return true
    } else {
      // 降级方案：使用 execCommand
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      const result = document.execCommand('copy')
      document.body.removeChild(textArea)
      return result
    }
  } catch (error) {
    console.error('复制失败:', error)
    return false
  }
}

/**
 * 复制指令实现
 */
export const copy: Directive = {
  /**
   * 元素挂载时
   */
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const { value, modifiers } = binding

    const handleClick = async () => {
      let textToCopy = ''

      if (typeof value === 'string') {
        textToCopy = value
      } else if (typeof value === 'function') {
        textToCopy = value()
      } else {
        textToCopy = el.textContent || el.innerText || ''
      }

      if (!textToCopy) {
        ElMessage.warning('没有可复制的内容')
        return
      }

      const success = await copyToClipboard(textToCopy)

      if (success) {
        if (modifiers.silent) {
          // 静默模式，不显示提示
          return
        }
        ElMessage.success(modifiers.message || '复制成功')
      } else {
        ElMessage.error('复制失败')
      }
    }

    el.addEventListener('click', handleClick)
    el._copyHandler = handleClick

    // 添加样式提示
    if (!modifiers.plain) {
      el.style.cursor = 'pointer'
      el.title = '点击复制'
    }
  },

  /**
   * 指令值更新时
   */
  updated(el: HTMLElement, binding: DirectiveBinding) {
    // 复制指令通常不需要在更新时做特殊处理
    // 因为复制的内容会在点击时动态获取
  },

  /**
   * 元素卸载时
   */
  unmounted(el: HTMLElement & { _copyHandler?: () => void }) {
    if (el._copyHandler) {
      el.removeEventListener('click', el._copyHandler)
      delete el._copyHandler
    }
  }
}
